import { createContext, useContext, useLayoutEffect } from 'react';

/**
 * "Chromeless" = hide the app's big header so a page can fill the viewport (the CRM list
 * grid). App owns the state and provides the setter here; a full-viewport page calls `useChromeless()`
 * to hide the chrome while it's mounted and restore it on unmount. Default is a no-op so the hook is
 * safe outside a provider.
 */
export const ChromelessContext = createContext<(hidden: boolean) => void>(() => {});

export function useChromeless() {
  const setHidden = useContext(ChromelessContext);
  // useLayoutEffect (not useEffect) so the chrome is gone before paint — no header flash on open.
  useLayoutEffect(() => {
    setHidden(true);
    // apply the #root full-viewport class SYNCHRONOUSLY here (not via a passive effect) so it's in
    // effect for the pre-paint viewport-fill measurement in this same commit — otherwise #root's
    // bottom padding is counted as chrome below and the first paint squishes before it expands.
    // Call this hook BEFORE useViewportFill so this layout effect runs first (adv-review).
    document.body.classList.add('crmFullscreen');
    return () => {
      setHidden(false);
      document.body.classList.remove('crmFullscreen');
    };
  }, [setHidden]);
}
